import { apiConstants } from "@data/constants/apiConstants"
import { brevetFunnel } from "@data/redux/reducers/data/brevetFunnel"
import chapters from "@data/redux/reducers/data/chapters"
import interviewReports from "@data/redux/reducers/data/interviewReports"
import { liveReports } from "@data/redux/reducers/data/liveReports"
import liveSessions from "@data/redux/reducers/data/liveSessions"
import materials from "@data/redux/reducers/data/materials"
import referrals from "@data/redux/reducers/data/referrals"
import schools from "@data/redux/reducers/data/schools"
import studyPrograms from "@data/redux/reducers/data/studyPrograms"
import { combineReducers } from "redux"
import { authTokens } from "./authTokens"
import backOffice from "./backOffice"
import { cancelFlow } from "./cancelFlow"
import currentActivity from "./currentActivity"
import pendingAttachments from "./pendingAttachments"
import { currentUser } from "./currentUser"
import { family } from "./family"
import formData from "./formData"
import reviewer from "./reviewer"
import schoolCardReport from "./schoolCardReport"
import { session } from "./session"
import { staticData } from "./staticData"
import { studyMissions } from "./studyMissions"
import { sam } from "./sam"
import { summer } from "./summer"
import { summerFunnel } from "./summerFunnel"
import { stageFunnel } from "./stageFunnel"
import { inscriptionFunnel } from "./inscriptionFunnel"
import { tutorEventsPanel } from "./tutorEventsPanel"
import { tutorLiveSession } from "./tutorLiveSession"
import suggestions from "./suggestions"
import { tutorAvailability } from "./tutorAvailability"
import { tutorTraining } from "./tutorTraining"
import { tutorMonthlyScore } from "./tutorMonthlyScore"
import { tutorPresence } from "./tutorPresence"
import { tutorOverrunAck } from "./tutorOverrunAck"
import { tutorSlotMoves } from "./tutorSlotMoves"
import tutors from "./tutors"
import users from "./users"
import videoCall from "./videoCall"
import schoolAssessments from "./schoolAssessments"
import { authService } from "@data/services/authService"
import { subscriptionService } from "@data/services/subscriptionService"

const dataReducers = combineReducers({
    authTokens,
    currentUser,
    suggestions,
    reviewer,
    session,
    tutors,
    tutorMonthlyScore,
    tutorAvailability,
    tutorTraining,
    tutorPresence,
    tutorOverrunAck,
    tutorSlotMoves,
    users,
    staticData,
    formData,
    videoCall,
    backOffice,
    referrals,
    liveReports,
    chapters,
    schools,
    interviewReports,
    studyPrograms,
    materials,
    liveSessions,
    schoolCardReport,
    currentActivity,
    pendingAttachments,
    family,
    schoolAssessments,
    studyMissions,
    sam,
    summer,
    summerFunnel,
    stageFunnel,
    inscriptionFunnel,
    cancelFlow,
    brevetFunnel,
    tutorEventsPanel,
    tutorLiveSession,
})

/**
 * Une lecture d'identité décrit le compte dont elle portait le jeton : `GET /api/user`, et le tarif
 * quand il ne vise pas un e-mail. Revenue après une bascule de compte (`fromPreviousSession`, posé
 * par `apiRequest`), elle décrit le compte d'avant : l'écrire ramènerait l'enfant d'avant dans
 * `currentUser` ou `formData.user`, et l'accueil du nouvel enfant attendrait une identité qui ne vient
 * plus. Elle n'écrit donc rien, dans aucune tranche — c'est la lecture partie sous le jeton du compte
 * connecté qui écrit.
 */
const IDENTITY_READS = [authService.getUser].map(
    (service) => service.name + apiConstants._SUCCESS
)

function describesPreviousAccount(action) {
    if (!action.fromPreviousSession) return false
    if (IDENTITY_READS.includes(action.type)) return true
    return (
        action.type ===
            subscriptionService.getSubscribePricing.name +
                apiConstants._SUCCESS && !action.urlParams?.email
    )
}

// Les tranches du compte affiché : celles qui se vident au changement de compte (`SWITCH_ACCOUNT`),
// et, dans `formData`, la copie du compte que lisent le portail Abonnement et l'accueil parent.
const DISPLAYED_ACCOUNT_SLICES = [
    "currentUser",
    "liveSessions",
    "liveReports",
    "summer",
    "tutorSlotMoves",
]
const DISPLAYED_ACCOUNT_FORM_FIELDS = [
    "user",
    "billingPortalUrl",
    "pauseBalance",
]

/**
 * Le compte affiché est celui du jeton de la session. Toute autre réponse partie sous un autre jeton
 * décrit un autre compte : les tranches du compte affiché gardent ce qu'elles avaient. Deux
 * exceptions : une lecture qui vise un e-mail, et une réponse qui change la session elle-même (une
 * fin de session, quel que soit le jeton parti) — le compte affiché change alors avec elle.
 */
function keepDisplayedAccount(state, next, action) {
    if (!action.fromPreviousSession || action.urlParams?.email || !state)
        return next
    if (next.session !== state.session) return next
    const slices = DISPLAYED_ACCOUNT_SLICES.filter(
        (key) => next[key] !== state[key]
    )
    const fields = DISPLAYED_ACCOUNT_FORM_FIELDS.filter(
        (key) => next.formData?.[key] !== state.formData?.[key]
    )
    if (slices.length === 0 && fields.length === 0) return next
    const kept = { ...next }
    slices.forEach((key) => (kept[key] = state[key]))
    if (fields.length > 0) {
        kept.formData = { ...next.formData }
        fields.forEach((key) => (kept.formData[key] = state.formData[key]))
    }
    return kept
}

export default function data(state, action) {
    if (describesPreviousAccount(action)) return state
    return keepDisplayedAccount(state, dataReducers(state, action), action)
}
